import React from 'react';
import { Link } from 'react-router-dom';
import { FiArrowLeft, FiMail, FiMessageCircle, FiExternalLink, FiGlobe, FiCode } from 'react-icons/fi';
import { SiApple, SiWindows } from 'react-icons/si';
import './SupportIndexPage.css';

type AppSupportItem = {
  slug: string;
  name: string;
  description: string;
  platform: string;
  icon: React.ReactNode;
  status: string;
};

const SUPPORT_APPS: AppSupportItem[] = [
  {
    slug: 'civics-tutor',
    name: 'Civics Tutor 2025',
    description: 'Study and pass the U.S. Citizenship civics exam with official USCIS questions.',
    platform: 'iOS',
    icon: <SiApple size={24} />,
    status: 'In Review'
  },
  {
    slug: 'home-cookbook',
    name: 'Home Cookbook',
    description: 'Recipe manager and meal planner for organizing recipes and creating shopping lists.',
    platform: 'iOS',
    icon: <SiApple size={24} />,
    status: 'Coming Soon'
  },
  {
    slug: 'gridmp3',
    name: 'GridMP3',
    description: 'Powerful MP3 tag editor built for DJs to manage large music libraries.',
    platform: 'Windows & Mac',
    icon: (
      <div style={{ display: 'flex', gap: '6px' }}>
        <SiWindows size={20} />
        <SiApple size={20} />
      </div>
    ),
    status: 'Coming Soon'
  },
  {
    slug: 'locknkey',
    name: 'Lock-n-Key',
    description: 'Password manager for KeePass databases across Windows, Mac, and iPhone.',
    platform: 'Windows, Mac & iOS',
    icon: (
      <div style={{ display: 'flex', gap: '6px' }}>
        <SiWindows size={20} />
        <SiApple size={20} />
      </div>
    ),
    status: 'Coming Soon'
  },
  {
    slug: 'footy-creator',
    name: 'Footy Creator',
    description: 'Content portal for soccer creators with stats, formations, and community features.',
    platform: 'Web',
    icon: <FiGlobe size={24} />,
    status: 'Live'
  },
  {
    slug: 'token-kit',
    name: 'token-kit',
    description: 'AI platform for developers to integrate LLMs powered by consumer-purchased tokens.',
    platform: 'Developer Platform',
    icon: <FiCode size={24} />,
    status: 'Live Beta'
  },
  {
    slug: 'token-kit-sdk',
    name: 'token-kit SDK',
    description: 'Open-source SDK for integrating token-kit into JavaScript/TypeScript applications.',
    platform: 'NPM Package',
    icon: <FiCode size={24} />,
    status: 'Beta Release'
  },
  {
    slug: 'ai-tokens',
    name: 'AI Tokens',
    description: 'Purchase and manage tokens for apps in the token-kit ecosystem.',
    platform: 'Web',
    icon: <FiGlobe size={24} />,
    status: 'Live Beta'
  },
];

const SupportIndexPage: React.FC = () => {
  return (
    <main className="support-index">
      <div className="support-index__hero">
        <div className="container">
          <Link to="/" className="support-index__back">
            <FiArrowLeft /> Back to Home
          </Link>
          <h1 className="support-index__title">Support Center</h1>
          <p className="support-index__subtitle">
            Get help with our apps or contact our support team directly.
          </p>
        </div>
      </div>

      <div className="container support-index__body">
        <section className="support-index__apps-section">
          <h2>App Support</h2>
          <p className="support-index__section-desc">
            Select an app below to view FAQs, troubleshooting guides, and app-specific support.
          </p>

          <div className="support-index__apps-grid">
            {SUPPORT_APPS.map((app) => (
              <Link
                to={`/support/${app.slug}`}
                key={app.slug}
                className="support-index__app-card"
              >
                <div className="support-index__app-icon">
                  {app.icon}
                </div>
                <div className="support-index__app-info">
                  <h3 className="support-index__app-name">{app.name}</h3>
                  <p className="support-index__app-platform">{app.platform}</p>
                  <p className="support-index__app-desc">{app.description}</p>
                  <div className="support-index__app-link-text">
                    View Support <FiExternalLink size={14} />
                  </div>
                </div>
                <div className="support-index__app-status">
                  {app.status}
                </div>
              </Link>
            ))}
          </div>
        </section>

        <section className="support-index__contact-section">
          <h2>General Support</h2>
          <p className="support-index__section-desc">
            Need help with something else? Reach out to our support team directly.
          </p>

          <div className="support-index__contact-grid">
            <div className="support-index__contact-card">
              <div className="support-index__contact-icon">
                <FiMail size={28} />
              </div>
              <h3>Email Support</h3>
              <p>
                For technical support, bug reports, or app-specific questions.
              </p>
              <a
                href="mailto:support@codetunezstudios.com"
                className="support-index__contact-button"
              >
                <FiMail size={16} />
                support@codetunezstudios.com
              </a>
            </div>

            <div className="support-index__contact-card">
              <div className="support-index__contact-icon">
                <FiMessageCircle size={28} />
              </div>
              <h3>General Inquiries</h3>
              <p>
                For general questions, feedback, or partnership opportunities.
              </p>
              <a
                href="mailto:contact@codetunezstudios.com"
                className="support-index__contact-button"
              >
                <FiMessageCircle size={16} />
                contact@codetunezstudios.com
              </a>
            </div>
          </div>
        </section>

        <section className="support-index__resources-section">
          <h2>Additional Resources</h2>
          <div className="support-index__resources-grid">
            <Link to="/privacy" className="support-index__resource-item">
              <h4>Privacy Policy</h4>
              <p>Learn how we protect and handle your data</p>
            </Link>
            <Link to="/blog" className="support-index__resource-item">
              <h4>Blog</h4>
              <p>Latest updates, tutorials, and announcements</p>
            </Link>
            <a
              href="https://github.com/CodeTunez-Studios"
              target="_blank"
              rel="noopener noreferrer"
              className="support-index__resource-item"
            >
              <h4>GitHub</h4>
              <p>Open source projects and documentation</p>
            </a>
          </div>
        </section>

        <section className="support-index__response-info">
          <div className="support-index__info-box">
            <h3>📧 Response Time</h3>
            <p>
              We typically respond to all support inquiries within <strong>24-72 hours</strong> during business days.
              For urgent issues, please include "URGENT" in your email subject line.
            </p>
          </div>
        </section>
      </div>
    </main>
  );
};

export default SupportIndexPage;
